import React, { useRef } from 'react';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { DndProvider, useDrop, useDrag } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
const widthClasses = {
    1: 'one',
    2: 'two',
    3: 'three',
    4: 'four',
    5: 'five',
    6: 'six',
    7: 'seven',
    8: 'eight',
    9: 'nine',
    10: 'ten',
    11: 'eleven',
    12: 'twelve',
    13: 'thirteen',
    14: 'fourteen',
    15: 'fifteen',
    16: 'sixteen',
};
function semanticWidthClass(width) {
    if (!width)
        return '';
    if (typeof width === 'number')
        return `${widthClasses[width] || ''} wide`.trim();
    return '';
}
const DragHandle = styled.div.attrs({
    className: 'fluid-drag-handle'
}) `
  cursor: grab;
  padding: 4px;
  color: #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #999;
  }
  
  &:active {
    cursor: grabbing;
  }
`;
const DraggableRow = styled.tr.attrs({
    className: 'fluid-draggable-row'
}).withConfig({
    shouldForwardProp: (prop) => !['isDragging', 'isOver'].includes(prop),
}) `
  transition: background-color 0.2s ease;
  
  ${props => props.isDragging && `
    background-color:rgb(254, 255, 200);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    opacity: 0.5;
  `}

  ${props => props.isOver && `
    background-color:rgb(254, 255, 200); 
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    opacity: 0.5;
  `}
`;
const DraggableTableRow = ({ item, index, columns, onMoveRow, onDragStart, onDragEnd, }) => {
    const ref = useRef(null);
    const [{ isDragging, originalIndex }, drag, preview] = useDrag({
        type: 'row',
        item: () => {
            console.log('Drag started:', { index });
            //onDragStart(item);
            return { index, originalIndex: index };
        },
        end: (draggedItem, monitor) => {
            const finalIndex = draggedItem?.finalDropIndex ?? draggedItem?.index;
            console.log('Drag ended:', {
                draggedItem,
                originalIndex: draggedItem?.originalIndex,
                currentIndex: draggedItem?.index,
                finalDropIndex: draggedItem?.finalDropIndex,
                finalIndex,
                didDrop: monitor.didDrop()
            });
            if (draggedItem) {
                // Use finalDropIndex if available, otherwise fall back to current index
                onDragEnd(draggedItem.originalIndex, finalIndex);
            }
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
            originalIndex: monitor.getItem()?.originalIndex,
        }),
    });
    const [{ handlerId, isOver }, drop] = useDrop({
        accept: 'row',
        hover: (draggedItem, monitor) => {
            if (!ref.current) {
                return;
            }
            const dragIndex = draggedItem.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex)
                return;
            console.log('Hover - updating draggedItem.index from', dragIndex, 'to', hoverIndex);
            // Update the dragged item's current index
            //draggedItem.index = hoverIndex;
            // Update visual state
            //onMoveRow(dragIndex, hoverIndex);
        },
        drop: (draggedItem) => {
            console.log('Drop on index', index, 'draggedItem:', draggedItem);
            // Set the final drop index
            draggedItem.finalDropIndex = index;
            onMoveRow(draggedItem.originalIndex, index);
            return { dropIndex: index };
        },
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
                isOver: monitor.isOver(),
            };
        },
    });
    //drag(drop(ref))
    drop(preview(ref));
    return (React.createElement(DraggableRow, { isDragging: isDragging, isOver: isOver, "data-handler-id": handlerId, ref: ref },
        React.createElement("td", { className: "fluid-table-cell" },
            React.createElement(DragHandle, { ref: drag },
                React.createElement(Icon, { name: "bars" }))),
        columns.map(column => (React.createElement("td", { key: column.key, className: "fluid-table-cell" }, column.render(item, index))))));
};
export const DragAndDropTable = ({ data, columns, onOrderChange, footer, compact = false, celled = false }) => {
    const moveRowInternal = (fromIndex, toIndex) => {
        // Update internal state for visual feedback
        const newData = [...data];
        const [movedItem] = newData.splice(fromIndex, 1);
        newData.splice(toIndex, 0, movedItem);
    };
    const handleDragStart = (item) => {
        console.log('handleDragStart called:', { item });
    };
    const handleDragEnd = (fromIndex, toIndex) => {
        console.log('handleDragEnd called:', { fromIndex, toIndex });
        // Only call onOrderChange when drag is complete
        if (fromIndex !== toIndex) {
            console.log('Calling onOrderChange:', { fromIndex, toIndex });
            onOrderChange(fromIndex, toIndex);
        }
        else {
            console.log('No change - fromIndex equals toIndex');
        }
    };
    const tableContent = (React.createElement("table", { className: `ui table fluid-table ${celled ? 'celled' : ''} ${compact ? 'compact' : ''}` },
        React.createElement("thead", null,
            React.createElement("tr", null,
                React.createElement("th", { className: `fluid-table-header ${semanticWidthClass(1)}` }),
                columns.map(column => (React.createElement("th", { key: column.key, className: `fluid-table-header ${semanticWidthClass(column.width)}` }, column.header))))),
        React.createElement("tbody", null, data.map((item, index) => (React.createElement(DraggableTableRow, { key: index, item: item, index: index, columns: columns, onMoveRow: moveRowInternal, onDragStart: handleDragStart, onDragEnd: handleDragEnd })))),
        footer && (React.createElement("tfoot", null, footer))));
    return (React.createElement(DndProvider, { backend: HTML5Backend }, tableContent));
};
